"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Check } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  highlightExample,
  type ExampleFormat,
} from "@/lib/docs/nexum/highlight";
import { requestExamples } from "@/lib/docs/nexum/request-examples";
import { TryRequest } from "./try-request";
import * as Tabs from "@radix-ui/react-tabs";
export function ApiExamples({
  method,
  path,
  example,
  server,
  responseExample,
  responseStatus,
  requiresAuth,
}: {
  method: string;
  path: string;
  example: string;
  server: string;
  responseExample: string;
  responseStatus: string;
  requiresAuth: boolean;
}) {
  const examplesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const examples = examplesRef.current;
    const details = examples
      ?.closest(".api-operation-columns")
      ?.querySelector<HTMLElement>(".api-operation-details");
    const tabs = examples?.querySelector<HTMLElement>(".api-example-tabs");
    if (!examples || !details || !tabs) return;
    const measure = () => {
      const gap = parseFloat(getComputedStyle(examples).rowGap) || 0;
      const available =
        details.getBoundingClientRect().height -
        tabs.getBoundingClientRect().height -
        gap;
      examples.style.setProperty(
        "--api-example-max-height",
        `${Math.max(320, Math.floor(available))}px`,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(details);
    observer.observe(tabs);
    return () => observer.disconnect();
  }, []);
  const [format, setFormat] = useState<ExampleFormat>("HTTP");
  const [copied, setCopied] = useState("");
  const [copyError, setCopyError] = useState(false);
  const resetCopy = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (resetCopy.current) clearTimeout(resetCopy.current);
    },
    [],
  );
  const snippets = useMemo(
    () => requestExamples({ method, server, path, example, requiresAuth }),
    [method, server, path, example, requiresAuth],
  );
  const code = snippets[format as keyof typeof snippets];
  async function copy(text: string, key: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      if (resetCopy.current) clearTimeout(resetCopy.current);
      resetCopy.current = setTimeout(() => setCopied(""), 1800);
      setCopyError(false);
    } catch {
      setCopyError(true);
    }
  }
  function copyButton(text: string, key: string) {
    return (
      <button
        type="button"
        className="api-copy api-code-copy"
        aria-label={copied === key ? `Copied ${key}` : `Copy ${key}`}
        onClick={() => copy(text, key)}
      >
        <span className="relative size-3.5">
          <Copy
            size={14}
            className={`absolute inset-0 transition-[opacity,transform] duration-200 motion-reduce:transition-none ${copied === key ? "scale-75 opacity-0" : "scale-100 opacity-100"}`}
          />
          <Check
            size={14}
            className={`absolute inset-0 text-green-700 transition-[opacity,transform] duration-200 motion-reduce:transition-none dark:text-green-400 ${copied === key ? "scale-100 opacity-100" : "scale-75 opacity-0"}`}
          />
        </span>
      </button>
    );
  }
  return (
    <Tabs.Root
      ref={examplesRef}
      defaultValue="request"
      className="api-examples"
    >
      <Tabs.List
        className="api-example-tabs"
        aria-label={`Examples for ${method} ${path}`}
      >
        <Tabs.Trigger value="request">Request</Tabs.Trigger>
        {responseExample && (
          <Tabs.Trigger value="response">
            Response <span className="api-status">{responseStatus}</span>
          </Tabs.Trigger>
        )}
      </Tabs.List>
      <Tabs.Content value="request" className="api-code-card">
        <div className="api-code-toolbar">
          <span className={`api-method api-method-${method.toLowerCase()}`}>
            {method}
          </span>
          <code
            className="min-w-0 flex-1 truncate font-mono text-xs"
            title={path}
          >
            {path}
          </code>
          <Select
            value={format}
            onValueChange={(value) => setFormat(value as ExampleFormat)}
          >
            <SelectTrigger
              aria-label={`Request language for ${path}`}
              className="h-8 w-28 shrink-0 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="HTTP">HTTP</SelectItem>
              <SelectItem value="cURL">cURL</SelectItem>
              <SelectItem value="JavaScript">JavaScript</SelectItem>
              <SelectItem value="Python">Python</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="api-code-body">
          {copyButton(code, "request example")}
          <pre tabIndex={0} aria-label="Request example">
            <Code text={code} format={format} />
          </pre>
        </div>
        <div className="api-code-footer">
          <TryRequest
            requiresAuth={requiresAuth}
            method={method}
            path={path}
            example={example}
          />
        </div>
      </Tabs.Content>
      {responseExample && (
        <Tabs.Content value="response" className="api-code-card">
          <div className="api-code-toolbar">
            <span className="api-status">{responseStatus}</span>
            <span className="flex-1">Response example</span>
          </div>
          <div className="api-code-body">
            {copyButton(responseExample, "response example")}
            <pre tabIndex={0} aria-label="Response example">
              <Code text={responseExample} format="JSON" />
            </pre>
          </div>
        </Tabs.Content>
      )}
      {copyError && (
        <p role="status" className="text-xs text-muted">
          Couldn’t copy. Select the example text to copy it.
        </p>
      )}
    </Tabs.Root>
  );
}
function Code({ text, format }: { text: string; format: ExampleFormat }) {
  const highlighted = useMemo(
    () => highlightExample(text, format),
    [text, format],
  );
  return (
    <code className="hljs" dangerouslySetInnerHTML={{ __html: highlighted }} />
  );
}
